import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { Editor } from "@tiptap/react";

export type DraftSaveState = "idle" | "saving" | "saved" | "error";

type UseDraftAutosaveProps = {
  enabled: boolean;
  //builds the draft payload from the current form/editor state and persists it
  persist: () => Promise<void> | void;
  //TipTap editor whose content is part of the draft but lives outside the form state
  editor?: Editor | null;
  debounceMs?: number;
};

//debounced draft autosave, extracted from the DocumentEditorPanel pattern
const useDraftAutosave = ({ enabled, persist, editor = null, debounceMs = 750 }: UseDraftAutosaveProps) => {
  const [saveState, setSaveState] = useState<DraftSaveState>("idle");
  const persistRef = useRef(persist);
  persistRef.current = persist;
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());

  const debouncedPersistRef = useRef(
    _.debounce(() => {
      //chain saves so a slow PATCH cannot overtake a newer one
      saveQueueRef.current = saveQueueRef.current
        .then(async () => {
          await persistRef.current();
          setSaveState("saved");
        })
        .catch(() => setSaveState("error"));
    }, debounceMs)
  );

  const scheduleSave = () => {
    if (!enabled) {
      return;
    }
    setSaveState("saving");
    debouncedPersistRef.current();
  };

  useEffect(() => {
    //flush a pending save on unmount so edits are not lost when the drawer closes
    return () => {
      debouncedPersistRef.current.flush();
    };
  }, []);

  useEffect(() => {
    if (!editor || !enabled) {
      return;
    }
    editor.on("update", scheduleSave);
    return () => {
      editor.off("update", scheduleSave);
    };
  }, [editor]);

  return { saveState, scheduleSave };
};

export default useDraftAutosave;
